import { Form } from 'components';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { useClientByIdQuery, useClientManagementMutation } from 'api';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { IManagementEditModal } from './index';

export const useManagementEditModal = (props: IManagementEditModal) => {
  const { onModalStateChange, modalState } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { handleSuccess, handleError } = useNotificationMessages();
  const { data: clientDetails = {} } = useClientByIdQuery({ id });
  const [updateClientManagement, { isLoading }] = useClientManagementMutation();

  const initialManagement: Record<string, string> = useMemo(
    () => ({
      salesManagerId: clientDetails.salesManagerId,
      salesManagerAssistantId: clientDetails.salesManagerAssistantId,
      purchaseManagerId: clientDetails.purchaseManagerId,
      purchaseManagerAssistantId: clientDetails.purchaseManagerAssistantId,
    }),
    [clientDetails]
  );
  const handleManagerChange = useCallback(
    (value: string, key: string) => {
      if (value === form.getFieldValue([key])) {
        form.setFieldsValue({ [key]: '' });
      }
    },
    [form]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const onFinish = useCallback(async () => {
    try {
      const payload = form.getFieldsValue() || {};
      const isFormChanged = Object.keys(payload).some((key) => initialManagement[key] !== payload[key]);

      if (!isFormChanged) {
        onCancel();
        return;
      }

      payload.clientId = id as string;
      const { data, error }: Record<string, any> = await updateClientManagement(payload);
      if (data?.success) {
        handleSuccess('Client management was successfully updated.');
        onCancel();
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [updateClientManagement, handleError, handleSuccess, id, onCancel, form, initialManagement]);

  useEffect(() => {
    if (initialManagement && modalState) {
      form.setFieldsValue(initialManagement);
    }
  }, [form, modalState, initialManagement]);

  return {
    onFinish,
    onCancel,
    handleManagerChange,
    form,
    isLoading,
  };
};
